import type { RoleCode } from '../../rbac/types';

/** Permission required to open Task/Case Details in update mode. */
export const TASK_CASE_UPDATE_PERMISSION = 'ASSIGNED_OFFICER_VIEW';

/** Permission that grants update mode for Allocation Officer users. */
export const ALLOCATION_OFFICER_UPDATE_PERMISSION = 'ALLOCATION_OFFICER_VIEW';

/** Roles that may open Task/Case Details in update mode. */
export const TASK_CASE_UPDATE_ROLE_CODES: ReadonlySet<RoleCode> = new Set([
  'COUNSEL',
  'ATTORNEY_GENERAL',
  'SOLICITOR_GENERAL',
  'JUNIOR_COUNSEL',
  // roleNameToRoleCode maps counsel-type role names to ASSIGNED_OFFICER
  'ASSIGNED_OFFICER',
  'ALLOCATION_OFFICER',
  'SUPERVISING_OFFICER',
  'STATE_ATTORNEY',
  'OFFICE_CLERK',
]);

/**
 * Update mode when the user matches an allowed role OR has ASSIGNED_OFFICER_VIEW
 * (same role-or-permission rule as route access elsewhere in the app).
 */
export function canUpdateTaskCaseDetails(
  roleCode: RoleCode,
  hasPermission: (code: string) => boolean,
): boolean {
  if (TASK_CASE_UPDATE_ROLE_CODES.has(roleCode)) return true;
  if (hasPermission(TASK_CASE_UPDATE_PERMISSION)) return true;
  return hasPermission(ALLOCATION_OFFICER_UPDATE_PERMISSION);
}

/** Query param used when opening Task/Case Details on a specific tab (e.g. Case Update). */
export const TASK_CASE_DETAILS_TAB_PARAM = 'detailsTab';

export type TaskCaseDetailsTabId = 'cd' | 'cu';

export function buildTaskCaseDetailsPath(input: {
  caseRequestId: string;
  userTaskId?: string;
  viewMode?: boolean;
  detailsTab?: TaskCaseDetailsTabId;
}): string {
  const caseRequestId = input.caseRequestId.trim();
  const base = `/tasks/${encodeURIComponent(caseRequestId)}`;
  const qs = new URLSearchParams();
  const userTaskId = (input.userTaskId ?? '').trim();
  if (userTaskId) qs.set('userTaskId', userTaskId);
  if (input.viewMode) qs.set('mode', 'view');
  if (!input.viewMode && input.detailsTab === 'cu') {
    qs.set(TASK_CASE_DETAILS_TAB_PARAM, 'cu');
  }
  const q = qs.toString();
  return q ? `${base}?${q}` : base;
}

export function readTaskCaseDetailsTabFromSearchParams(
  searchParams: URLSearchParams,
  viewMode: boolean,
): TaskCaseDetailsTabId {
  if (viewMode) return 'cd';
  return searchParams.get(TASK_CASE_DETAILS_TAB_PARAM) === 'cu' ? 'cu' : 'cd';
}

/** Force view mode on task/case detail paths when the user cannot update. */
export function applyViewModeToTaskCaseDetailsPath(
  path: string,
  roleCode: RoleCode,
  hasPermission: (code: string) => boolean,
): string {
  if (!path.startsWith('/tasks/')) return path;
  if (canUpdateTaskCaseDetails(roleCode, hasPermission)) return path;

  const url = new URL(path, 'http://local');
  if (url.searchParams.get('mode') === 'view') return path;
  url.searchParams.set('mode', 'view');
  return `${url.pathname}${url.search}`;
}
